import { DatePipe } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ALERT_TYPE } from 'src/app/constants';
import { GlobalSearchCustomerComponent } from 'src/app/Others/global-search-customer/global-search-customer.component';
import { Enums } from 'src/app/Shared/enums';
import { SharedService } from 'src/app/Shared/shared-service.service';
import { EPFLimit } from 'src/model/epfLimit.model';
import { GlobalCustomer } from 'src/model/globalCustomer.model';
import { Loan } from 'src/model/loan.model';
import { SettlementCharges } from 'src/model/settlementCharges.model';
import { EpfService } from 'src/services/epf.service';
import { LoanService } from 'src/services/loan.service';

@Component({
    selector: 'app-epf-request-calculator',
    templateUrl: './epf-request-calculator.component.html',
    styleUrls: ['./epf-request-calculator.component.scss'],
    providers: [DatePipe],
    standalone: false
})
export class EpfRequestCalculatorComponent {
  private activeModal = inject(NgbActiveModal);
  protected sharedService = inject(SharedService);
  protected enums = inject(Enums);
  private datepipe = inject(DatePipe);
  private epfService = inject(EpfService);
  private loanService = inject(LoanService);

  @Input() epfLimitRuleSet: EPFLimit[];

  epfDate: Date;
  epfAmount: number;

  loanTerm = 0;
  loanAmount = 0;
  settlementAmount = 0;
  interestRate = 0;

  processStamp = 0;
  processSc = 0;
  processDL = 0;

  isCollapsed = false;

  isExtra = false;
  MINIMUM_INTEREST = 0.05

  // previousPrincipal = 0;
  previousEPFLoans: Loan[] = [];
  customer: GlobalCustomer;

  cashOnHand: number;

  extraChargesCollected = 0;
  settlementCharges: SettlementCharges;
  totalDeductions = 0;

  settlementStamp = 0;
  settlementSC = 0;
  settlementExtras = 0;

  summaryOpenState = true;

  plannedExtraPrincipal = 0;
  plannedExtraSettlementCharges = 0;
  plannedSettlementCharges: SettlementCharges;

  maximumFirstPrincipalByOnHand = 0;
  maximumExtraPrincipal = 0;
  maximumFirstCashOnHand = 0;
  isPlannedExtra = false;

  calculatedBalancePlannedExtra = 0;

  calculateLoanAmountByCOH() {
    this.loanAmount = this.sharedService.calculateRoundedPrincipalByOnHand(this.cashOnHand);
    this.setMaximumExtraCharges();

    this.updateMaxPlannedExtraPrincipal();

    if ((this.cashOnHand == 0 || this.cashOnHand == undefined) && this.isPlannedExtra == true)
      this.resetPlannedExtra();
  }

  updateMaxPlannedExtraPrincipal() {
    const settlement = this.calculateSettlementAmountWithMinimumCharges(this.loanAmount, this.MINIMUM_INTEREST, false);
    this.calculatedBalancePlannedExtra = this.calculateMaximumExtraPrincipal(this.loanAmount, settlement, this.loanTerm);
  }

  setMaximumExtraCharges() {
    //set maximum extra charge
    this.extraChargesCollected = this.enums.MIN_SSC_PER_TERM * this.loanTerm;
    this.getSettlementCharges();
  }

  checkIsMinimumCharges(charge: number) {
    if (charge == this.enums.MIN_SSC_PER_TERM * this.loanTerm)
      return true
    else
      return false
  }

  useFirstMaxPlan() {
    this.isPlannedExtra = true;
    this.loanAmount = this.maximumFirstPrincipalByOnHand;
    this.cashOnHand = this.maximumFirstCashOnHand;
    this.plannedExtraPrincipal = this.maximumExtraPrincipal;
    this.plannedExtraSettlementCharges = this.enums.MIN_SSC_PER_TERM * this.loanTerm;
    this.getPlannedSettlementCharges();
    this.setMaximumExtraCharges();
    this.calculatedBalancePlannedExtra = this.maximumExtraPrincipal;
  }

  checkIsMaxPlan() {
    const firstLoanSame = (this.loanAmount == this.maximumFirstPrincipalByOnHand);
    const extraLoanSame = (this.plannedExtraPrincipal == this.maximumExtraPrincipal);
    return this.isExtra == false && firstLoanSame && extraLoanSame;
  }

  getFirstPlanTMaxTotal() {
    return this.maximumFirstPrincipalByOnHand + this.maximumExtraPrincipal;
  }

  getFirstPlanMaxSettlement() {
    const firstLoanInterest = this.MINIMUM_INTEREST;
    const plannedExtraLoanInterst = this.enums.MINIMUM_EXTRA_EPF_RATE;
    return this.calculateSettlementAmountWithMinimumCharges(this.maximumFirstPrincipalByOnHand, firstLoanInterest, false) + this.calculateSettlementAmountWithMinimumCharges(this.maximumExtraPrincipal, plannedExtraLoanInterst, true)
  }

  getFirstPlanMaxOnHand() {
    const firstLoanOnHand = this.sharedService.getEPFLimitCashOnHand(this.epfDate, this.epfAmount,
      this.maximumFirstPrincipalByOnHand);
    const plannedExtraOnHand = this.maximumExtraPrincipal - this.calculateMinimumDeductionWithoutDL(this.maximumExtraPrincipal);

    return firstLoanOnHand + plannedExtraOnHand;
  }

  getUserFirstPlannedPrincipals() {
    return this.loanAmount + (this.plannedExtraPrincipal || 0)
  }

  getUserFirstPlannedSettlement() {
    const userFirstSettlement = (this.calculateTotalSettlement(this.loanAmount) + this.calculateTotalCharges() || 0);
    const extraInterestRate = this.enums.MINIMUM_EXTRA_EPF_RATE * 100;
    const userPlannedExtraSettlementWOCharge = (this.sharedService.calculateEPFSettlementAmount(this.plannedExtraPrincipal, extraInterestRate, this.loanTerm));
    const userPlannedExtraSettlement = (userPlannedExtraSettlementWOCharge == 0) ? 0 : userPlannedExtraSettlementWOCharge + (this.plannedExtraSettlementCharges || 0) + this.calculateHandlingCharge(true, this.plannedExtraPrincipal);

    return userFirstSettlement + userPlannedExtraSettlement;
  }

  getUserFirstPlannedOnHand() {
    const userFirstOnHand = this.sharedService.getEPFLimitCashOnHand(this.epfDate, this.epfAmount, this.loanAmount);
    const userPlannedExtraOnHand = (this.plannedExtraPrincipal > 0) ? this.plannedExtraPrincipal - this.calculateMinimumDeductionWithoutDL((this.plannedExtraPrincipal || 0)) : 0;
    return userFirstOnHand + userPlannedExtraOnHand;
  }

  calculateFirstLoanPlan() {
    const maximumFirstPrincipal = this.sharedService.getEPFLimitFirstLoan(this.epfDate, this.epfAmount, this.epfLimitRuleSet);
    this.maximumFirstCashOnHand = this.sharedService.getEPFLimitCashOnHand(this.epfDate, this.epfAmount, maximumFirstPrincipal);
    this.maximumFirstPrincipalByOnHand = this.sharedService.calculateRoundedPrincipalByOnHand(this.maximumFirstCashOnHand);

    const term = this.sharedService.getEPFTerm(this.epfDate);

    if (this.maximumFirstCashOnHand > 0) {
      const firstLoanSettlementCharges = this.enums.MIN_SSC_PER_TERM * term;
      const maximumFirstLoanSettlement = this.sharedService.calculateEPFSettlementAmount(this.maximumFirstPrincipalByOnHand, this.MINIMUM_INTEREST * 100, term) + firstLoanSettlementCharges;

      this.maximumExtraPrincipal = this.calculateMaximumExtraPrincipal(this.maximumFirstPrincipalByOnHand, maximumFirstLoanSettlement, this.loanTerm);
      this.calculatedBalancePlannedExtra = this.maximumExtraPrincipal;
    }
  }

  calculateMaximumExtraPrincipal(firstPrincipal: number, firstSettlement: number, term: number) {
    const maxExtraByLimit = this.sharedService.getEPFLimitPrincipal(this.epfDate, this.epfAmount, this.epfLimitRuleSet) - firstPrincipal;

    const settlementAfterFirstLoan = this.sharedService.getEPFLimitSettlement(this.epfDate, this.epfAmount, this.epfLimitRuleSet) - firstSettlement;
    const maxExtraBySettlement = Number(this.sharedService.calculatePrincipalBySettlementLeft(settlementAfterFirstLoan, term, true));

    return (maxExtraByLimit < maxExtraBySettlement) ? this.sharedService.calculateRoundedPrincipal(maxExtraByLimit) : this.sharedService.calculateRoundedPrincipal(maxExtraBySettlement);
  }

  calculateSettlementAmountWithMinimumCharges(principal: number, interestRate: number, isExtra: boolean) {
    if (principal > 0) {
      const minimumSettlementCharge = this.enums.MIN_SSC_PER_TERM * this.loanTerm;
      return this.sharedService.calculateEPFSettlementAmount(principal, (interestRate * 100), this.loanTerm) + minimumSettlementCharge + this.calculateHandlingCharge(isExtra, principal);
    }
    else
      return 0;
  }

  resetExtra() {
    // this.isExtra = !this.isExtra;
    this.customer = undefined;
    this.epfAmount = undefined;
    this.epfDate = undefined;
    this.loanTerm = undefined;
    this.previousEPFLoans = [];
    this.settlementCharges = undefined;
    this.settlementStamp = undefined;
    this.settlementSC = undefined;
    this.settlementExtras = undefined;
    this.totalDeductions = undefined;
    this.extraChargesCollected = undefined;

    this.maximumFirstPrincipalByOnHand = undefined;
    this.maximumExtraPrincipal = undefined;
  }

  async getCustomerLoanInfo(customer: GlobalCustomer) {
    this.previousEPFLoans = [];
    this.previousEPFLoans = await this.loanService.getCustomerLoans(customer.CustomerID, { status: [this.enums.ACTIVE_LOAN], schemes: ['EPF'], withLegacyEPF: true, sharedProfileId: customer.SharedProfileId, includedSharedProfile: true });
    if (this.previousEPFLoans.length == 0) {
      this.sharedService.openAlert("This profile do not have active EPF loan(s)", ALERT_TYPE.DANGER);
      this.resetExtra();
    } else {
      this.getCustomerEPFInfo(customer.CustomerID);
    }
  }

  checkIfHittingRatioLimit(epfDate: Date, epfAmount: number, previousEPFLoans?: Loan[]) {
    if (this.epfDate != undefined && epfAmount != undefined) {
      const previousAmount = this.sharedService.calculatePreviousEPFLoanSettlement(previousEPFLoans) ?? 0;
      const currentSettlement = this.sharedService.roundToDec((this.calculateTotalSettlement(this.loanAmount) + this.calculateTotalCharges()), 2);
      const limitMaximumSettlement = this.sharedService.roundToDec(this.sharedService.getEPFLimitSettlement(epfDate, epfAmount, this.epfLimitRuleSet), 2);

      //if there's previous epf loans remember to add previous settlement amount to get actual ratio limit
      if (currentSettlement + previousAmount > limitMaximumSettlement)
        return true;
    }
    return false;
  }

  async getCustomerEPFInfo(customerID: number) {
    try {
      const epf = await this.epfService.getCustomerEPF(customerID);
      this.epfDate = epf.Date;
      this.epfAmount = epf.Amount;

      if (this.epfDate < new Date()) {
        this.sharedService.openAlert("EPF Date already over! EPF DATE:  " + this.datepipe.transform(this.epfDate, "dd-MM-yyyy"), ALERT_TYPE.DANGER)
        this.resetExtra();
      } else {
        this.calculateLoanTerm();
      }
    } catch (error) {
      this.reset();
    }
  }

  changeInterestRate(reset: boolean) {
    if (this.isExtra == true) {
      if (reset == true)
        this.resetExtra();
      else
        this.MINIMUM_INTEREST = this.enums.MINIMUM_EXTRA_EPF_RATE;
    }

    else {
      if (reset == true)
        this.resetExtra();
      if (this.loanTerm <= 3)
        this.MINIMUM_INTEREST = this.enums.MINIMUM_EXTRA_EPF_RATE;
      else
        this.MINIMUM_INTEREST = this.enums.MINIMUM_NEW_EPF_RATE;
    }
  }

  calculateLoanTerm() {
    this.loanTerm = this.sharedService.getEPFTerm(this.epfDate);
    this.changeInterestRate(false)
  }

  getLoanCalculatedProperties() {
    this.calculateLoanTerm();
    if (this.isExtra == false)
      this.calculateFirstLoanPlan();
  }

  calculateMinimumDeduction(principal: number, isExtra: boolean) {
    if (isExtra == true)
      return this.processStamp + this.processSc + (principal * this.enums.MINIMUM_ADS_RATE) + this.processDL + (principal * (this.enums.FIXED_SCHEME_PROCESSING_RATE / 100));
    else
      return this.enums.MINIMUM_STAMP + this.enums.MINIMUM_SC + (principal * this.enums.MINIMUM_ADS_RATE) + (this.sharedService.calculateRoundedDLByOnHand(this.cashOnHand) || 0) + (principal * (this.enums.FIXED_SCHEME_PROCESSING_RATE / 100));
  }

  calculateMinimumDeductionWithoutDL(principal: number) {
    return this.enums.MINIMUM_STAMP + this.enums.MINIMUM_SC + (principal * this.enums.MINIMUM_ADS_RATE) + (principal * (this.enums.FIXED_SCHEME_PROCESSING_RATE / 100));
  }

  calculateTotalSettlement(principal: number, userDefinedInterestRate?: number) {
    const interestRate = (userDefinedInterestRate == undefined) ? this.MINIMUM_INTEREST : userDefinedInterestRate;

    const settlement = (interestRate * principal * this.loanTerm) + principal;
    return settlement
  }

  calculateHandlingCharge(isExtra: boolean, loanAmount: number) {
    if (isExtra == true)
      return Math.floor(this.enums.MININUM_HANDLING_RATE * loanAmount * this.loanTerm);
    else
      return 0;
  }

  calculateTotalCharges() {
    if (this.isExtra == true)
      return this.settlementStamp + this.settlementSC + this.settlementExtras + this.calculateHandlingCharge(this.isExtra, this.loanAmount);
    else
      return this.enums.MIN_SSC_PER_TERM * this.loanTerm;
  }

  calculateMinimumSettlement(principal: number, userDefinedInterestRate?: number) {
    const ir = (userDefinedInterestRate == undefined) ? this.MINIMUM_INTEREST : userDefinedInterestRate;

    return this.calculateTotalSettlement(this.loanAmount, userDefinedInterestRate) + this.calculateTotalCharges();
  }

  searchCustomer() {
    const modalRef = this.sharedService.openModal(GlobalSearchCustomerComponent, "largeModal")
    modalRef.componentInstance.isGlobal = this.sharedService.isAdmin() || this.sharedService.isBossAccount();
    modalRef.componentInstance.needRedirect = false;

    modalRef.componentInstance.passEntry.subscribe((entry) => {
      this.customer = entry;
      this.getCustomerLoanInfo(this.customer);
    });
  }

  reset() {
    this.activeModal.close();
  }

  calculateSettlementCharges(isExtra: boolean, principal: number, collectedAmount: number) {
    let settlementCharges: SettlementCharges;
    if (isExtra == true)
      settlementCharges = this.sharedService.calculateAndReturnSettlementCharges(principal, collectedAmount, this.loanTerm);
    else
      settlementCharges = this.sharedService.calculateEPFFirstLoanSettlementCharges(collectedAmount, this.loanTerm);

    return settlementCharges;
  }

  getPlannedSettlementCharges() {
    this.plannedSettlementCharges = this.calculateSettlementCharges(true, this.plannedExtraPrincipal, this.plannedExtraSettlementCharges);
    // console.log(this.plannedSettlementCharges)
  }

  async getSettlementCharges() {
    this.settlementCharges = await this.calculateSettlementCharges(this.isExtra, this.loanAmount, this.extraChargesCollected);

    this.settlementStamp = this.settlementCharges.Stamp;
    this.settlementSC = this.settlementCharges.ServiceCharge;
    this.settlementExtras = (this.settlementCharges.Extras == undefined) ? 0 : this.settlementCharges.Extras;

    if (this.isExtra == true)
      this.calculateProcessDeductions();
  }

  calculateProcessDeductions() {
    this.processStamp = this.enums.MINIMUM_STAMP;
    this.processSc = this.enums.MINIMUM_SC;
  }

  resetPlannedExtra() {
    this.isPlannedExtra = !this.isPlannedExtra;
    if (this.isPlannedExtra == false) {
      this.plannedExtraPrincipal = 0;
      this.plannedExtraSettlementCharges = 0;
      this.plannedSettlementCharges = undefined;
    }
  }

  isCashOnHandOverlimit() {
    return this.cashOnHand > this.sharedService.getEPFLimitCashOnHand(this.epfDate, this.epfAmount, this.sharedService.getEPFLimitFirstLoan(this.epfDate, this.epfAmount, this.epfLimitRuleSet));
  }

  isPlannedExtraOverlimit() {
    return this.plannedExtraPrincipal > this.calculatedBalancePlannedExtra;
  }

  isPlannedSettlementChargesOverlimit() {
    const settlementDiff = (this.plannedExtraPrincipal == 0) ? 0 : this.getFirstPlanMaxSettlement() - this.getUserFirstPlannedSettlement();

    if (settlementDiff < 0)
      return true;
    else
      return false;
  }
}
